import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { Autocomplete } from '../components/Autocomplete';
import { SearchableSelect } from '../components/SearchableSelect';
import { ConfirmModal } from '../components/ConfirmModal';
import { EditableEntityTable } from '../components/EditableEntityTable';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EntityDetailRow,
} from '../components/EditableEntityTable';
import { ListViewPanel } from '@quoruum/frontend-common/components/ListViewPanel';
import type { ListViewItem } from '@quoruum/frontend-common/components/ListViewPanel';
import type {
  EntityField,
  ListPage,
  EntityQueryParams,
  EntityName,
  EntitySchema,
  RelatedList,
  RelatedListEdit,
} from '../schemas/base/EntitySchema';
import { organizationSchema } from '../schemas/organization';
import { compSetSchema } from '../schemas/compSet';
import { userSchema } from '../schemas/user';

const ENTITY_PARAM = 'entity';
const ID_PARAM = 'id';
const PARENT_PARAM = 'parent';
const ORG_PARAM = 'orgId';
const PARENT_PAGE_SIZE = 20;
const MIN_PARENT_SEARCH_LENGTH = 2;
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 250;
const RELATED_PAGE_SIZE = 25;
// Header plus 10 rows; taller pages scroll inside this box.
const RELATED_TABLE_MAX_HEIGHT_CLASS = 'max-h-[412px]';

const QUERY_KEY_ROOT = 'entityExplorer';

function getPath(entity: object, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (value, part) => (value && typeof value === 'object' ? Reflect.get(value, part) : undefined),
      entity
    );
}

/** A schema listed by the explorer on its own: it needs a list and a get-by-id. */
type ExplorableSchema<TEntity extends object> = EntitySchema<TEntity> & {
  endpoints: {
    list: (params: EntityQueryParams) => Promise<ListPage>;
    read: (id: string) => Promise<TEntity>;
  };
};

type EntityRegistration = {
  name: EntityName;
  label: string;
  /** Whether the explorer shows an organization picker for this entity. */
  supportsOrganization: boolean;
  render: (organizationId?: string) => ReactElement;
};

// Binding the entity type here lets each list render fully typed without erasing it.
function register<TEntity extends object>(schema: EntitySchema<TEntity>): EntityRegistration {
  const { name } = schema;
  const { list, read } = schema.endpoints;
  if (schema.selector) {
    return {
      name,
      label: schema.label,
      supportsOrganization: schema.selector.supportsOrganization,
      render: (organizationId) => (
        <ParentListView schema={schema} organizationId={organizationId} />
      ),
    };
  }
  if (!list || !read) {
    throw new Error(`Entity "${name}" needs a list and read endpoint to be explored.`);
  }
  const explorable: ExplorableSchema<TEntity> = {
    ...schema,
    endpoints: { ...schema.endpoints, list, read },
  };
  return {
    name,
    label: schema.label,
    supportsOrganization: false,
    render: () => <EntityListView schema={explorable} />,
  };
}

const ENTITY_REGISTRATIONS: ReadonlyArray<EntityRegistration> = [
  register(organizationSchema),
  register(userSchema),
  register(compSetSchema),
];

function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function formatValue(field: { type?: string }, value: unknown): string {
  const text = toText(value);
  if (!text) return '-';
  if (field.type === 'datetime') {
    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? text : date.toLocaleString();
  }
  return text;
}

function toFieldSpec(field: EntityField): EditableFieldSpec {
  switch (field.type) {
    case 'textarea':
      return { editType: 'textarea' };
    case 'number':
      return { editType: 'number', parseAsInteger: field.integer };
    case 'boolean':
      return { editType: 'checkbox' };
    case 'enum':
      return { editType: 'select', options: field.options, allowEmptyOption: true };
    default:
      return { editType: 'text' };
  }
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timeout);
  }, [value, delayMs]);
  return debounced;
}

function EntityListView<TEntity extends object>({
  schema,
}: {
  schema: ExplorableSchema<TEntity>;
}): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get(ID_PARAM) ?? undefined;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  const listQuery = useInfiniteQuery({
    queryKey: [QUERY_KEY_ROOT, schema.label, 'list', debouncedSearch],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      schema.endpoints.list({
        search: debouncedSearch || undefined,
        page: pageParam,
        pageSize: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });
  const items = useMemo<ListViewItem[]>(
    () =>
      (listQuery.data?.pages ?? []).flatMap((page) =>
        page.entries.map((entry) => ({
          id: entry.id,
          primaryText: entry.primaryText || '(unnamed)',
          secondaryText: entry.secondaryText || undefined,
        }))
      ),
    [listQuery.data]
  );

  function selectItem(item: ListViewItem | null): void {
    const next = new URLSearchParams(searchParams);
    if (item) next.set(ID_PARAM, item.id);
    else next.delete(ID_PARAM);
    setSearchParams(next);
  }

  return (
    <ListViewPanel
      items={items}
      searchValue={search}
      onSearchChange={setSearch}
      onItemClick={selectItem}
      onLoadMore={() => void listQuery.fetchNextPage()}
      hasMore={Boolean(listQuery.hasNextPage)}
      isLoading={listQuery.isFetching}
      selectedItemId={selectedId}
      searchPlaceholder={`Search ${schema.label.toLowerCase()}...`}
    >
      {selectedId ? (
        <EntityDetail
          key={`${schema.label}:${selectedId}`}
          schema={schema}
          id={selectedId}
          load={() => schema.endpoints.read(selectedId)}
          onDeleted={() => selectItem(null)}
        />
      ) : (
        <p className="text-sm text-slate-500">Select an item to view its details.</p>
      )}
    </ListViewPanel>
  );
}

function ParentListView<TEntity extends object>({
  schema,
  organizationId,
}: {
  schema: EntitySchema<TEntity>;
  organizationId?: string;
}): ReactElement | null {
  const selector = schema.selector;
  const [searchParams, setSearchParams] = useSearchParams();
  const parentId = searchParams.get(PARENT_PARAM) ?? undefined;
  const selectedId = searchParams.get(ID_PARAM) ?? undefined;
  const [parentSearch, setParentSearch] = useState('');
  const [parentLabel, setParentLabel] = useState<string | null>(null);
  const debouncedSearch = useDebouncedValue(parentSearch.trim(), SEARCH_DEBOUNCE_MS);
  const isParentSelected = parentId !== undefined && parentLabel !== null;

  const parentQuery = useQuery({
    queryKey: [QUERY_KEY_ROOT, schema.label, 'parents', debouncedSearch],
    queryFn: () =>
      selector?.search({ search: debouncedSearch, page: 1, pageSize: PARENT_PAGE_SIZE }),
    enabled: debouncedSearch.length >= MIN_PARENT_SEARCH_LENGTH && !isParentSelected,
  });
  const entriesQuery = useQuery({
    queryKey: [QUERY_KEY_ROOT, schema.label, 'items', parentId, organizationId],
    queryFn: () => selector?.list(parentId, organizationId) ?? [],
    enabled: parentId !== undefined || organizationId !== undefined,
  });
  const entries = entriesQuery.data ?? [];
  const selectedEntry = entries.find((entry) => entry.id === selectedId);
  const items = useMemo<ListViewItem[]>(
    () =>
      entries.map((entry) => ({
        id: entry.id,
        primaryText: entry.primaryText || '(unnamed)',
        secondaryText: entry.secondaryText || undefined,
      })),
    [entries]
  );

  if (!selector) return null;

  function updateParams(change: (params: URLSearchParams) => void): void {
    const next = new URLSearchParams(searchParams);
    change(next);
    setSearchParams(next);
  }

  function clearParent(): void {
    setParentLabel(null);
    updateParams((params) => {
      params.delete(PARENT_PARAM);
      params.delete(ID_PARAM);
    });
  }

  const search = (
    <Autocomplete
      value={isParentSelected ? parentLabel : parentSearch}
      placeholder={`Search ${selector.parentLabel.toLowerCase()}...`}
      onValueChange={(value) => {
        setParentSearch(value);
        if (isParentSelected || parentId !== undefined) clearParent();
      }}
      onEscapeKey={() => {
        setParentSearch('');
        clearParent();
      }}
      options={isParentSelected ? [] : (parentQuery.data?.entries ?? [])}
      minSearchLength={MIN_PARENT_SEARCH_LENGTH}
      searchLength={debouncedSearch.length}
      isLoading={parentQuery.isFetching}
      loadingLabel={`Searching ${selector.parentLabel.toLowerCase()}s`}
      noResultsText={`No matching ${selector.parentLabel.toLowerCase()}s.`}
      containerClassName="min-w-0 flex-1"
      getOptionKey={(option) => option.id}
      renderOption={({ option }) => (
        <>
          <span className="font-medium text-slate-900">{option.primaryText}</span>
          {option.secondaryText && (
            <span className="ml-2 text-slate-500">{option.secondaryText}</span>
          )}
        </>
      )}
      onSelectOption={(option) => {
        const label = [option.primaryText, option.secondaryText].filter(Boolean).join(', ');
        setParentLabel(label || option.id);
        updateParams((params) => {
          params.set(PARENT_PARAM, option.id);
          params.delete(ID_PARAM);
        });
      }}
    />
  );

  return (
    <ListViewPanel
      items={items}
      searchValue=""
      onSearchChange={() => undefined}
      searchSlot={search}
      onItemClick={(item) => updateParams((params) => params.set(ID_PARAM, item.id))}
      onLoadMore={() => undefined}
      hasMore={false}
      isLoading={entriesQuery.isFetching}
      selectedItemId={selectedId}
      emptyMessage={
        parentId === undefined && organizationId === undefined
          ? `Search for a ${selector.parentLabel.toLowerCase()} to see its ${schema.label.toLowerCase()}s.`
          : `No ${schema.label.toLowerCase()}s found.`
      }
    >
      {selectedEntry ? (
        <EntityDetail
          key={`${schema.label}:${parentId}:${selectedEntry.id}`}
          schema={schema}
          id={selectedEntry.id}
          load={() => Promise.resolve(selectedEntry.item)}
          onDeleted={() => updateParams((params) => params.delete(ID_PARAM))}
        />
      ) : (
        <p className="text-sm text-slate-500">
          Select a {schema.label.toLowerCase()} to view its details.
        </p>
      )}
    </ListViewPanel>
  );
}

function RelatedEditRow({
  edit,
  initial,
  columnCount,
  isSaving,
  error,
  onSave,
  onCancel,
}: {
  edit: RelatedListEdit;
  initial: object;
  columnCount: number;
  isSaving: boolean;
  error: unknown;
  onSave: (values: Readonly<Record<string, EditableFieldValue>>) => void;
  onCancel: () => void;
}): ReactElement {
  const [values, setValues] = useState<Record<string, EditableFieldValue>>(() =>
    Object.fromEntries(
      edit.fields.map((field) => {
        const value = getPath(initial, field.key);
        const initialValue: EditableFieldValue =
          typeof value === 'number' || typeof value === 'boolean' ? value : toText(value);
        return [field.key, initialValue];
      })
    )
  );
  const inputClass =
    'w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-800';

  return (
    <tr className="border-t border-slate-100 bg-slate-50">
      <td colSpan={columnCount} className="px-3 py-3">
        <div className="grid gap-3 sm:grid-cols-2">
          {edit.fields.map((field) => {
            const value = values[field.key];
            const setValue = (next: EditableFieldValue) =>
              setValues((current) => ({ ...current, [field.key]: next }));
            return (
              <label
                key={field.key}
                className="flex flex-col gap-1 text-xs font-medium text-slate-600"
              >
                {field.label}
                {field.type === 'enum' ? (
                  <select
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => setValue(event.target.value)}
                    className={inputClass}
                  >
                    <option value="" />
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'number' ? (
                  <input
                    type="number"
                    step={field.integer ? 1 : 'any'}
                    value={typeof value === 'number' || typeof value === 'string' ? value : ''}
                    onChange={(event) =>
                      setValue(event.target.value === '' ? '' : Number(event.target.value))
                    }
                    className={inputClass}
                  />
                ) : field.type === 'boolean' ? (
                  <input
                    type="checkbox"
                    checked={value === true}
                    onChange={(event) => setValue(event.target.checked)}
                    className="h-4 w-4"
                  />
                ) : (
                  <input
                    type="text"
                    value={typeof value === 'string' ? value : ''}
                    onChange={(event) => setValue(event.target.value)}
                    className={inputClass}
                  />
                )}
              </label>
            );
          })}
        </div>
        {error !== null && error !== undefined && (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {getApiErrorMessage(error, 'Unable to save.')}
          </p>
        )}
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => onSave(values)}
            disabled={isSaving}
            className="cursor-pointer rounded-md bg-sky-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

function RelatedListPanel({
  list,
  parentId,
}: {
  list: RelatedList;
  parentId: string;
}): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const query = useQuery({
    queryKey: [QUERY_KEY_ROOT, 'related', list.label, parentId],
    queryFn: () => list.fetch(parentId),
    enabled: isOpen,
  });
  const [page, setPage] = useState(1);
  // `index` is undefined for a row being added.
  const [editing, setEditing] = useState<{ index?: number } | null>(null);
  const saveMutation = useMutation({
    mutationFn: async (variables: {
      index: number | undefined;
      values: Readonly<Record<string, EditableFieldValue>>;
    }) => {
      await list.edit?.save(parentId, variables.index, variables.values);
    },
    onSuccess: async () => {
      setEditing(null);
      await query.refetch();
    },
  });
  // Rows with an entity id are deleted by id, others by their position in the list.
  const [pendingDelete, setPendingDelete] = useState<{
    entityId?: string;
    index?: number;
  } | null>(null);
  const deleteMutation = useMutation({
    mutationFn: async (target: { entityId?: string; index?: number }) => {
      if (target.entityId !== undefined) await list.delete?.(target.entityId);
      else if (target.index !== undefined) await list.deleteRow?.(parentId, target.index);
    },
    onSuccess: async () => {
      setPendingDelete(null);
      await query.refetch();
    },
  });
  const hasActions = Boolean(list.edit ?? list.delete ?? list.deleteRow);
  const items = query.data ?? [];
  const pageCount = Math.max(1, Math.ceil(items.length / RELATED_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * RELATED_PAGE_SIZE;
  const pageItems = items.slice(pageStart, pageStart + RELATED_PAGE_SIZE);

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          className="flex cursor-pointer items-center gap-2 py-2 pl-3 text-left text-sm font-semibold text-slate-800 hover:text-slate-950"
        >
          <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
          <span>
            {list.label}
            {query.data ? ` (${items.length})` : ''}
          </span>
        </button>
        {isOpen && list.edit && (
          <button
            type="button"
            onClick={() => {
              saveMutation.reset();
              setPage(1);
              setEditing({});
            }}
            disabled={editing !== null || !query.data}
            aria-label={`Add to ${list.label.toLowerCase()}`}
            title="Add"
            className="ml-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white text-lg leading-none text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            +
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          onClick={() => setIsOpen((open) => !open)}
          className="h-9 flex-1 cursor-pointer"
        />
      </div>
      {isOpen && (
        <div className="overflow-x-auto border-t border-slate-200">
          {query.isLoading ? (
            <p className="p-3 text-sm text-slate-500">Loading...</p>
          ) : query.isError ? (
            <p className="p-3 text-sm text-red-600">
              {getApiErrorMessage(query.error, `Unable to load ${list.label.toLowerCase()}.`)}
            </p>
          ) : items.length === 0 && editing === null ? (
            <p className="p-3 text-sm text-slate-500">No {list.label.toLowerCase()}.</p>
          ) : (
            <>
              <div className={`overflow-y-auto ${RELATED_TABLE_MAX_HEIGHT_CLASS}`}>
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-slate-600">
                    <tr>
                      {list.fields.map((field) => (
                        <th key={field.key} className="px-3 py-2 font-medium">
                          {field.label}
                        </th>
                      ))}
                      {hasActions && <th className="w-28 px-3 py-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {list.edit && editing?.index === undefined && editing !== null && (
                      <RelatedEditRow
                        edit={list.edit}
                        initial={list.edit.newItem()}
                        columnCount={list.fields.length + 1}
                        isSaving={saveMutation.isPending}
                        error={saveMutation.error}
                        onSave={(values) => saveMutation.mutate({ index: undefined, values })}
                        onCancel={() => setEditing(null)}
                      />
                    )}
                    {pageItems.map(({ item, entityId }, index) =>
                      list.edit && editing?.index === pageStart + index ? (
                        <RelatedEditRow
                          key={pageStart + index}
                          edit={list.edit}
                          initial={item}
                          columnCount={list.fields.length + 1}
                          isSaving={saveMutation.isPending}
                          error={saveMutation.error}
                          onSave={(values) =>
                            saveMutation.mutate({ index: pageStart + index, values })
                          }
                          onCancel={() => setEditing(null)}
                        />
                      ) : (
                        <tr key={pageStart + index} className="border-t border-slate-100">
                          {list.fields.map((field, fieldIndex) => {
                            const text = formatValue(field, getPath(item, field.key));
                            return (
                              <td key={field.key} className="px-3 py-2 text-slate-800">
                                {fieldIndex === 0 &&
                                entityId &&
                                ENTITY_REGISTRATIONS.some((r) => r.name === list.linkEntity) ? (
                                  <Link
                                    to={`?${ENTITY_PARAM}=${list.linkEntity}&${ID_PARAM}=${encodeURIComponent(entityId)}`}
                                    className="font-medium text-sky-700 hover:underline"
                                  >
                                    {text}
                                  </Link>
                                ) : (
                                  text
                                )}
                              </td>
                            );
                          })}
                          {hasActions && (
                            <td className="space-x-3 px-3 py-2 text-right">
                              {list.edit && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    saveMutation.reset();
                                    setEditing({ index: pageStart + index });
                                  }}
                                  disabled={editing !== null}
                                  className="cursor-pointer text-sky-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Edit
                                </button>
                              )}
                              {(entityId ? list.delete : list.deleteRow) && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    deleteMutation.reset();
                                    setPendingDelete(
                                      entityId ? { entityId } : { index: pageStart + index }
                                    );
                                  }}
                                  disabled={editing !== null}
                                  className="cursor-pointer text-red-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
              {items.length > RELATED_PAGE_SIZE && (
                <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-xs text-slate-600">
                  <span>
                    {pageStart + 1}-{pageStart + pageItems.length} of {items.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="cursor-pointer rounded-md border border-slate-300 bg-white px-2.5 py-1.5 font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage} of {pageCount}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage(currentPage + 1)}
                      disabled={currentPage >= pageCount}
                      className="cursor-pointer rounded-md border border-slate-300 bg-white px-2.5 py-1.5 font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      <ConfirmModal
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onSubmit={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete);
        }}
        title={`Delete ${list.entityLabel}?`}
        message="Are you sure?"
        ariaLabel="Confirm delete"
        isSubmitting={deleteMutation.isPending}
        isSubmitDisabled={deleteMutation.isPending}
        submitLabel="Delete"
        submittingLabel="Deleting..."
        errorMessage={
          deleteMutation.isError
            ? getApiErrorMessage(deleteMutation.error, 'Unable to delete.')
            : null
        }
      />
    </div>
  );
}

function EntityDetail<TEntity extends object>({
  schema,
  id,
  load,
  onDeleted,
}: {
  schema: EntitySchema<TEntity>;
  id: string;
  /** Loads the entity to show; an entity already in hand can be returned directly. */
  load: () => Promise<TEntity>;
  onDeleted: () => void;
}): ReactElement {
  const readQuery = useQuery({
    queryKey: [QUERY_KEY_ROOT, schema.label, 'read', id],
    queryFn: load,
  });
  const editEndpoint = schema.endpoints.edit;
  const deleteEndpoint = schema.endpoints.delete;
  const deleteMutation = useMutation({
    mutationFn: async () => {
      await deleteEndpoint?.(id);
    },
  });
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const entity = readQuery.data;

  const { rows, fieldDefinitions } = useMemo(() => {
    const definitions = new Map<string, EditableFieldSpec>();
    const detailRows = schema.fields.map((field): EntityDetailRow => {
      const raw = entity ? getPath(entity, field.key) : undefined;
      const row: EntityDetailRow = {
        id: field.key,
        key: field.label,
        value: field.display && entity ? field.display(entity) || '-' : formatValue(field, raw),
      };
      if (editEndpoint && !field.readOnly) {
        row.editableFieldKey = field.key;
        definitions.set(field.key, toFieldSpec(field));
        if (field.type === 'boolean') row.editableFieldBooleanValue = raw === true;
        else if (field.type === 'number')
          row.editableFieldNumberValue = typeof raw === 'number' ? raw : undefined;
        else row.editableFieldValue = toText(raw);
      }
      return row;
    });
    return { rows: detailRows, fieldDefinitions: definitions };
  }, [entity, schema, editEndpoint]);

  async function handleSave(fieldKey: string, value: EditableFieldValue): Promise<void> {
    if (!editEndpoint) return;
    try {
      await editEndpoint(id, fieldKey, value);
    } catch (error) {
      throw Object.assign(new Error(getApiErrorMessage(error, 'Unable to save.')), {
        cause: error,
      });
    }
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ROOT] });
  }

  async function handleDelete(): Promise<void> {
    if (!deleteEndpoint || !window.confirm(`Delete this ${schema.label.toLowerCase()}?`)) return;
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ROOT] });
      onDeleted();
    } catch (error) {
      setDeleteError(getApiErrorMessage(error, 'Unable to delete.'));
    }
  }

  if (readQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading...</p>;
  }
  if (readQuery.isError || !entity) {
    return (
      <p className="text-sm text-red-600">
        {getApiErrorMessage(readQuery.error, `Unable to load ${schema.label.toLowerCase()}.`)}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <EditableEntityTable
        rows={rows}
        fieldDefinitions={fieldDefinitions}
        onSave={handleSave}
        header={<span className="font-semibold">{schema.label}</span>}
      />
      {schema.relatedLists?.map((list) => (
        <RelatedListPanel key={list.label} list={list} parentId={id} />
      ))}
      {deleteEndpoint && (
        <div>
          <button
            type="button"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
            className="cursor-pointer rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
          >
            Delete
          </button>
          {deleteError && <p className="mt-2 text-sm text-red-600">{deleteError}</p>}
        </div>
      )}
    </div>
  );
}

export function EntityExplorer(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const schema =
    ENTITY_REGISTRATIONS.find((s) => s.name === searchParams.get(ENTITY_PARAM)) ?? null;

  const [orgSearch, setOrgSearch] = useState('');
  const orgId = searchParams.get(ORG_PARAM) ?? undefined;
  const [orgLabel, setOrgLabel] = useState<string | null>(null);
  // After a reload only the id is in the URL, so the label is read back from the organization.
  const orgReadQuery = useQuery({
    queryKey: [QUERY_KEY_ROOT, 'organization', orgId],
    queryFn: async () => {
      const org = await organizationSchema.endpoints.read?.(orgId ?? '');
      return org?.label || org?.name || orgId;
    },
    enabled: orgId !== undefined && orgLabel === null,
  });
  const selectedOrgLabel = orgLabel ?? orgReadQuery.data ?? orgId;

  function setOrgId(id: string | undefined, label: string | null): void {
    setOrgLabel(label);
    const next = new URLSearchParams(searchParams);
    if (id === undefined) next.delete(ORG_PARAM);
    else next.set(ORG_PARAM, id);
    next.delete(ID_PARAM);
    setSearchParams(next);
  }
  const debouncedOrgSearch = useDebouncedValue(orgSearch.trim(), SEARCH_DEBOUNCE_MS);
  const showOrganization = Boolean(schema?.supportsOrganization);
  const orgQuery = useQuery({
    queryKey: [QUERY_KEY_ROOT, 'organizations', debouncedOrgSearch],
    queryFn: () =>
      organizationSchema.endpoints.list?.({
        search: debouncedOrgSearch,
        page: 1,
        pageSize: PARENT_PAGE_SIZE,
      }),
    enabled:
      showOrganization &&
      orgId === undefined &&
      debouncedOrgSearch.length >= MIN_PARENT_SEARCH_LENGTH,
  });

  function selectSchema(next: EntityRegistration): void {
    const params = new URLSearchParams();
    params.set(ENTITY_PARAM, next.name);
    if (orgId && next.supportsOrganization) params.set(ORG_PARAM, orgId);
    setSearchParams(params);
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-4">
      <div className="flex items-start gap-4">
        <SearchableSelect
          id="entity-select"
          options={ENTITY_REGISTRATIONS}
          value={schema}
          onChange={selectSchema}
          getOptionKey={(option) => option.name}
          getOptionLabel={(option) => option.label}
          placeholder="Select an entity..."
          noResultsText="No matching entities."
        />
        {showOrganization && (
          <Autocomplete
            id="organization-select"
            value={orgId ? (selectedOrgLabel ?? '') : orgSearch}
            placeholder="Filter by organization..."
            onValueChange={(value) => {
              setOrgSearch(value);
              if (orgId) setOrgId(undefined, null);
            }}
            onEscapeKey={() => {
              setOrgSearch('');
              setOrgId(undefined, null);
            }}
            options={orgId ? [] : (orgQuery.data?.entries ?? [])}
            minSearchLength={MIN_PARENT_SEARCH_LENGTH}
            searchLength={debouncedOrgSearch.length}
            isLoading={orgQuery.isFetching}
            loadingLabel="Searching organizations"
            noResultsText="No matching organizations."
            containerClassName="relative w-[400px] min-w-[200px] max-w-full"
            getOptionKey={(option) => option.id}
            renderOption={({ option }) => (
              <>
                <span className="font-medium text-slate-900">{option.primaryText}</span>
                {option.secondaryText && (
                  <span className="ml-2 text-slate-500">{option.secondaryText}</span>
                )}
              </>
            )}
            onSelectOption={(option) => setOrgId(option.id, option.primaryText || option.id)}
          />
        )}
      </div>
      <div className="min-h-0 flex-1">
        {schema ? (
          <Fragment key={schema.name}>
            {schema.render(showOrganization ? orgId : undefined)}
          </Fragment>
        ) : (
          <p className="text-sm text-slate-500">Select an entity to explore.</p>
        )}
      </div>
    </div>
  );
}
